import React, { useEffect, useState } from 'react';
import { evaluationsAPI } from '../../api/client';
import { getEvalIncidents } from '../../lib/evalSections';
import { getEvidenceUrl } from '../../lib/fileUrls';
import { ShieldAlert, Image, X } from 'lucide-react';
import { toast } from 'sonner';

const SEV_CONFIG = {
  Crítico: { bg: 'bg-red-100 text-red-800 border-red-200', dot: 'bg-red-600' },
  Mayor:   { bg: 'bg-orange-100 text-orange-800 border-orange-200', dot: 'bg-orange-500' },
};
const STATUS_CONFIG = {
  Abierto:      'bg-red-50 text-red-700 border-red-200',
  'En revisión': 'bg-yellow-50 text-yellow-700 border-yellow-200',
  Documentado:  'bg-green-50 text-green-700 border-green-200',
};

// #TemplateEvalRender — antes esto buscaba cada respuesta con
// `CRITERIA.find(cr => cr.id === item.criterio_id)`. Las evaluaciones hechas
// con plantilla guardan `question_id` y dejan `criterio_id` en null, así que
// ningún criterio hacía match y la bitácora del cliente salía siempre vacía
// aunque hubiera criterios en 0 o 2. getEvalIncidents resuelve ambos formatos.
export function deriveIncidents(evaluations) {
  const incidents = [];
  evaluations.forEach((ev) => {
    const evalName = ev.general_fields?.instalacion || `Evaluación v${ev.version}`;
    const date = ev.published_at
      ? new Date(ev.published_at).toLocaleDateString('es-MX')
      : new Date(ev.created_at).toLocaleDateString('es-MX');
    getEvalIncidents(ev).forEach((incident) => {
      incidents.push({
        ...incident,
          evaluationId: ev.id,
          recinto: evalName,
          version: ev.version,
        status: ev.status === 'publicada'
            ? 'Documentado'
        : 'Abierto',
        evidenceUrls: incident.evidence_urls,
        date,
      });
    });
  });
  return incidents.sort((a, b) => {
    if (a.severity === 'Crítico' && b.severity !== 'Crítico') return -1;
    if (b.severity === 'Crítico' && a.severity !== 'Crítico') return 1;
    return 0;
  });
}

// #HallazgosCliente-1.1 — antes armaba `${API_BASE}${url}`; desde que las
// evidencias viven en Cloudinary la URL ya es absoluta y el href quedaba
// "https://api.soempg.comhttps://res.cloudinary.com/..." (404). getEvidenceUrl
// respeta las absolutas y solo prefija las relativas viejas.
export function DetailModal({ incident, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-2xl animate-fade-in max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-slate-200 bg-soem-navy sticky top-0">
          <div>
            <h3 className="font-bold text-white">{incident.title}</h3>
            <p className="text-slate-400 text-xs mt-0.5">{incident.recinto} · v{incident.version}</p>
          </div>
          <button aria-label="Cerrar" onClick={onClose} className="text-slate-400 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          {/* Badges */}
          <div className="flex items-center gap-3">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border ${SEV_CONFIG[incident.severity]?.bg}`}>
              <span className={`w-2 h-2 rounded-full ${SEV_CONFIG[incident.severity]?.dot}`} />
              {incident.severity}
            </span>
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${STATUS_CONFIG[incident.status]}`}>
              {incident.status}
            </span>
            <span className="bg-slate-100 text-slate-600 text-xs px-2.5 py-1 rounded-full font-medium">{incident.section}</span>
            {incident.blocker && (
              <span className="bg-red-100 text-red-700 text-xs px-2.5 py-1 rounded-full font-bold">BLOQUEANTE</span>
            )}
          </div>

          {/* Description */}
          <div>
            <p className="soem-label mb-1">Descripción</p>
            <p className="text-slate-700 text-sm">{incident.description}</p>
          </div>

          {/* Recommendation */}
          <div className={`p-3 rounded-sm border-l-4 ${incident.severity === 'Crítico' ? 'bg-red-50 border-red-500' : 'bg-orange-50 border-orange-400'}`}>
            <p className="soem-label mb-1">{incident.severity === 'Crítico' ? 'Criterio Bloqueante' : 'Recomendación'}</p>
            <p className="text-sm text-slate-700">{incident.recommendation}</p>
          </div>

          {/* Score */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="soem-label mb-1">Calificación</p>
              <span className={`text-2xl font-black ${incident.score === 0 ? 'text-red-600' : 'text-orange-600'}`}>
                {incident.score}/10
              </span>
            </div>
            <div>
              <p className="soem-label mb-1">Fecha de Reporte</p>
              <p className="text-sm font-medium text-soem-navy">{incident.date}</p>
            </div>
            <div>
              <p className="soem-label mb-1">Criterio #</p>
              <p className="text-sm font-medium text-soem-navy">{incident.criterionId}</p>
            </div>
          </div>

          {/* Notes */}
          {incident.notes && (
            <div>
              <p className="soem-label mb-1">Notas del Evaluador</p>
              <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-sm border-l-2 border-slate-300">
                {incident.notes}
              </p>
            </div>
          )}

          {/* Evidence */}
          {incident.evidenceUrls?.length > 0 && (
            <div>
              <p className="soem-label mb-2">Evidencia Adjunta ({incident.evidenceUrls.length})</p>
              <div className="flex flex-wrap gap-3">
                {incident.evidenceUrls.map((url, i) => (
                  <a key={i} href={getEvidenceUrl(url)} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-xs text-soem-primary bg-blue-50 px-3 py-1.5 rounded-sm hover:bg-blue-100 border border-blue-200">
                    <Image size={12} /> Evidencia {i + 1}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function IncidentsPage() {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('all'); // all | Crítico | Mayor

  useEffect(() => {
    async function load() {
      try {
        const listRes = await evaluationsAPI.list();
        const evals = (listRes.data.items || []).filter(
          (e) => e.status === 'publicada' || e.status === 'en_revision'
        );
        // Load full details (with items) for each evaluation
        const fullEvals = await Promise.all(
          evals.map((e) => evaluationsAPI.get(e.id).then((r) => r.data).catch(() => null))
        );
        setIncidents(deriveIncidents(fullEvals.filter(Boolean)));
      } catch {
        toast.error('Error al cargar incidentes');
      } finally { setLoading(false); }
    }
    load();
  }, []);

  const filtered = filter === 'all' ? incidents : incidents.filter((i) => i.severity === filter);
  const critCount = incidents.filter((i) => i.severity === 'Crítico').length;
  const majCount = incidents.filter((i) => i.severity === 'Mayor').length;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="incidents-page">
      <div>
        <h1 className="text-2xl font-bold text-soem-navy">Bitácora de Incidentes</h1>
        <p className="text-slate-500 text-sm mt-1">Incidentes de severidad Mayor y Crítico de tus evaluaciones.</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div className="soem-card p-5 border-t-4 border-t-slate-400">
          <p className="soem-label mb-1">Total</p>
          <p className="text-3xl font-black text-soem-navy">{incidents.length}</p>
        </div>
        <div className="soem-card p-5 border-t-4 border-t-red-600">
          <p className="soem-label mb-1">Críticos</p>
          <p className="text-3xl font-black text-red-600">{critCount}</p>
        </div>
        <div className="soem-card p-5 border-t-4 border-t-orange-500">
          <p className="soem-label mb-1">Mayores</p>
          <p className="text-3xl font-black text-orange-600">{majCount}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        {[['all', 'Todos'], ['Crítico', 'Solo Críticos'], ['Mayor', 'Solo Mayores']].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)}
            className={`px-3 py-1.5 rounded-sm text-xs font-semibold border transition-colors ${filter === v ? 'bg-soem-primary text-white border-soem-primary' : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'}`}
            data-testid={`filter-${v}`}>
            {l}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="soem-card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-soem-primary border-t-transparent" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <ShieldAlert size={40} className="text-slate-200 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">No hay incidentes registrados para tu empresa.</p>
            <p className="text-slate-400 text-sm mt-1">Los incidentes aparecen cuando evaluaciones publicadas tienen criterios con calificación baja.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="soem-table-header">
                <th className="px-5 py-3 text-left">Título</th>
                <th className="px-5 py-3 text-left">Locación</th>
                <th className="px-5 py-3 text-left">Severidad</th>
                <th className="px-5 py-3 text-left">Estado</th>
                <th className="px-5 py-3 text-left">Fecha</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((inc) => (
                <tr key={inc.id} className="soem-table-row cursor-pointer"
                  onClick={() => setSelected(inc)} data-testid={`incident-row-${inc.id}`}>
                  <td className="px-5 py-3">
                    <p className="font-semibold text-soem-navy">{inc.title}</p>
                    <p className="text-slate-400 text-xs">{inc.section}</p>
                  </td>
                  <td className="px-5 py-3 text-slate-600">{inc.recinto} v{inc.version}</td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold border ${SEV_CONFIG[inc.severity]?.bg}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${SEV_CONFIG[inc.severity]?.dot}`} />
                      {inc.severity}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${STATUS_CONFIG[inc.status]}`}>
                      {inc.status}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-slate-500">{inc.date}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selected && <DetailModal incident={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
